import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { Link } from "react-router-dom";

import { Image } from "common";

import { URLS } from "@/constants/constant";
import { Icon } from "@/design-system";

/**
 * The reference footer (grep `============ FOOTER ============` in
 * `Dens N Dente Tailwind.dc.html`): blue-900 band, a `1.6fr + 3×1fr` grid — brand column
 * with the white logo, tagline and two filled trust rows, then link columns — and a
 * hairline-topped © bar. Its container is `max-w-container`, which matters beyond looks:
 * the `body:has([data-shell-wide])` rule widens every `max-w-container` together, so the
 * footer's edges now track the listing's wide shell instead of stopping 300px short
 * (that misalignment is the "shell edges" backlog entry — this is the footer half of it).
 *
 * Content divergences from the reference, all deliberate:
 *
 * - **Link columns point at routes that exist.** "Brands" → `/brands` since that page
 *   landed (it was omitted while the route didn't exist). The reference's "Open an
 *   account" (no application page) is still replaced by the app's real destinations:
 *   Clearance, and Pick up process. Same rule as the nav: add the route before the
 *   link, not the other way round.
 * - **A "Visit us" column carries the business's real address, phones and email** —
 *   content the reference never modelled. Phones are `tel:` links, the email `mailto:`.
 * - **The social row stays** (the reference has none): these are live brand presences.
 *   Brand marks have no Material Symbols equivalent, so the `react-icons` glyphs are
 *   the sanctioned exception to the icon rule here — don't "fix" them into look-alike
 *   symbols. The old Google+ link was dropped: it pointed at an unrelated Google blog
 *   post, Google+ having been shut down in 2019.
 * - **The © line keeps the real legal entity** ("Smiles First Corporation"), not the
 *   reference's placeholder attribution. "Accessibility" and "Staff sign in" from its
 *   bottom bar have no pages and are omitted.
 *
 * Internal links are router `<Link>`s. This retired `common/navigation/AppLink` — the
 * footer was its last consumer, and it is a plain `<a>`, so every footer click was a
 * full reload.
 */
const SHOP_LINKS = [
  { label: "All categories", to: "/categories" },
  { label: "Brands", to: "/brands" },
  { label: "Deals & offers", to: "/promotions" },
  { label: "Clearance", to: "/clearance" },
  { label: "Order again", to: "/re-order" },
];

// "Our story" / "Meet our team" / "Blog" absorb the navbar's old "About Us" menu
// (moved here on request) — which is also where the Aug 2026 reference footer puts
// its About us / Meet our team rows.
const HERE_LINKS = [
  { label: "Help center", to: "/faq" },
  { label: "Contact us", to: "/contact" },
  { label: "Returns & refunds", to: "/returns" },
  { label: "Invoices & statements", to: "/invoices" },
  { label: "Pick up process", to: "/pickup" },
  { label: "About us", to: "/about" },
  { label: "Meet our team", to: "/team" },
  { label: "Blog", to: "/blog" },
];

const SOCIAL_LINKS = [
  { label: "Facebook", href: URLS.SOCIAL.FACEBOOK, IconGlyph: FaFacebookF },
  { label: "Instagram", href: URLS.SOCIAL.INSTAGRAM, IconGlyph: FaInstagram },
  { label: "LinkedIn", href: URLS.SOCIAL.LINKEDIN, IconGlyph: FaLinkedinIn },
];

const columnLinkClass =
  "block px-0 py-[5px] font-sans text-sm text-blue-200 no-underline [transition:color_var(--duration-fast)] hover:text-white focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]";

const bottomLinkClass =
  "text-blue-300 no-underline [transition:color_var(--duration-fast)] hover:text-white focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]";

export default function Footer() {
  return (
    <footer className="mt-auto bg-blue-900 font-sans text-blue-100">
      <div className="mx-auto grid max-w-container grid-cols-1 gap-[var(--space-10)] px-[var(--gutter)] pb-[var(--space-12)] pt-[var(--space-16)] sm:grid-cols-2 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
        {/* Brand */}
        <div>
          <Image
            src="/logo-white.png"
            alt="Dens 'N Dente Healthcare"
            className="mb-[var(--space-4)] h-[44px] w-auto"
          />
          <p className="max-w-[42ch] text-sm leading-[1.6] text-blue-200">
            Care, supplied. Canada&apos;s relationship-first dental supply
            company.
          </p>
          <div className="mt-[var(--space-5)] flex flex-col gap-[var(--space-2)]">
            <span className="inline-flex items-center gap-[var(--space-2)] text-sm">
              <Icon
                name="verified"
                size={18}
                fill
                weight={400}
                className="text-blue-200"
              />
              Health Canada licensed distributor
            </span>
            <span className="inline-flex items-center gap-[var(--space-2)] text-sm">
              <Icon
                name="local_shipping"
                size={18}
                fill
                weight={400}
                className="text-blue-200"
              />
              Fast delivery right across Canada
            </span>
          </div>
          <div className="mt-[var(--space-5)] flex gap-[var(--space-4)]">
            {SOCIAL_LINKS.map(({ label, href, IconGlyph }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={label}
                className="text-blue-300 [transition:color_var(--duration-fast)] hover:text-white focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
              >
                <IconGlyph className="text-xl" />
              </a>
            ))}
          </div>
        </div>

        {/* Shop */}
        <div>
          <h4 className="mb-[var(--space-4)] text-sm font-semibold tracking-[var(--tracking-wide)] text-white">
            Shop
          </h4>
          {SHOP_LINKS.map(({ label, to }) => (
            <Link key={to} to={to} className={columnLinkClass}>
              {label}
            </Link>
          ))}
        </div>

        {/* We're here */}
        <div>
          <h4 className="mb-[var(--space-4)] text-sm font-semibold tracking-[var(--tracking-wide)] text-white">
            We&apos;re here
          </h4>
          {HERE_LINKS.map(({ label, to }) => (
            <Link key={to} to={to} className={columnLinkClass}>
              {label}
            </Link>
          ))}
        </div>

        {/* Visit us — the business's real contact block; the reference never modelled one */}
        <div>
          <h4 className="mb-[var(--space-4)] text-sm font-semibold tracking-[var(--tracking-wide)] text-white">
            Visit us
          </h4>
          <p className="py-[5px] text-sm text-blue-200">
            91 Granton Drive,
            <br />
            Richmond Hill, ON L4B 2N5
          </p>
          <a href="tel:1-866-449-9998" className={columnLinkClass}>
            1-866-449-9998
          </a>
          <a href="tel:905-475-3367" className={columnLinkClass}>
            905-475-3367
          </a>
          <p className="py-[5px] text-sm text-blue-200">Fax — 905-475-2894</p>
          <a href="mailto:info@densndente.ca" className={columnLinkClass}>
            info@densndente.ca
          </a>
        </div>
      </div>

      <div className="border-t border-[rgba(255,255,255,.1)]">
        <div className="mx-auto flex max-w-container flex-col gap-[var(--space-3)] px-[var(--gutter)] py-[var(--space-5)] text-sm text-blue-300 sm:flex-row sm:items-center sm:justify-between">
          <span>
            © {new Date().getFullYear()} Smiles First Corporation. All rights
            reserved.
          </span>
          <div className="flex gap-[var(--space-5)]">
            <Link to="/privacy-policy" className={bottomLinkClass}>
              Privacy
            </Link>
            <Link to="/terms" className={bottomLinkClass}>
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
